import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../UserContext';
import { useData } from '../../DataContext';
import { BACKEND_URL } from '../../config';
import { getVisitorId } from '../../utils/beacon';
import { User } from '../../compos/SinglePostHelper/SinglePostHelper';
import BReporterLogo from '../../Media/Icons/BReporter.png';
import AuthorityPicker, { QrAuthority } from './AuthorityPicker';
import PhotoViewer from './PhotoViewer';
import PermissionHelp, { PermissionProblem, cameraProblemFrom, locationProblemFrom } from './PermissionHelp';
import { DeviceOS, detectOS, openAppOrStore } from './appLinks';
import {
	buildRequiredFieldValues,
	isAutoInjectedField,
	isEntryNumberField,
	normaliseEntryNumber,
	prettyFieldLabel,
	specForReportField,
} from './reportFields';
import './QRReport.css';

// Flow: LANDING → CAMERA (photo + live location) → REVIEW → DETAILS
// (authority + template fields) → the site's /login or /signup if logged out
// (they send the user back here) → PREVIEW (confirm) → SUBMITTING → SUCCESS.
// PERMISSION and OUTSIDE are detours when location/camera is blocked or the
// photo was taken off campus.
type Step =
	'LANDING' | 'CAMERA' | 'PERMISSION' | 'REVIEW' | 'OUTSIDE' | 'DETAILS' | 'PREVIEW' | 'SUBMITTING' | 'SUCCESS';

interface QrOrg {
	key: string;
	fullName: string;
	logoUrl: string | null;
	authorities: QrAuthority[];
}

interface Fix {
	lat: number;
	lng: number;
	/** Metres, as reported by the browser (68% confidence radius). */
	accuracy: number;
	/** This device's clock when the server stamped the photo. Drives the
	 *  countdown only; the server judges the deadline from `stamp`. */
	capturedAt?: number;
	/** Server-signed capture time + location (POST /api/capture-stamp), sent
	 *  with the report. A photo is never kept without one. */
	stamp?: string;
}

interface Draft {
	authorityId: number | null;
	title: string;
	body: string;
	fields: Record<string, string>;
}

const MAX_PHOTO_EDGE = 1600;
const TITLE_MAX = 100;
const BODY_MAX = 1000;

// A report must be sent within this long of taking its photo; after that the
// draft is discarded and the page resets. The server enforces the same window
// from the capture stamp's own (server) time, so moving the phone's clock only
// changes what the countdown shows.
const DRAFT_TTL_MS = 10 * 60 * 1000;
const DRAFT_STEPS: Step[] = ['REVIEW', 'OUTSIDE', 'DETAILS', 'PREVIEW'];

// The shutter waits for a fix at least this precise, but only for
// ACCURACY_GRACE_MS after the first fix; then it takes the best it has.
const GOOD_ACCURACY_M = 50;
const ACCURACY_GRACE_MS = 10 * 1000;

// While the camera is open, location is re-checked every LOCATION_PROBE_MS.
// Without a fresh reading for LOCATION_STALE_MS (or with the location
// switched off) the shutter is disabled until it comes back.
const LOCATION_PROBE_MS = 5 * 1000;
const LOCATION_STALE_MS = 20 * 1000;
// How long "GPS acquired" stays up before it fades away.
const ACQUIRED_PILL_MS = 1500;

// One draft at a time, belonging to one QR code. It lives in localStorage so
// it survives closing the tab, the trip to /login or /signup, and a reload;
// DRAFT_TTL_MS bounds how long. `qr` records which QR it came from (org +
// pinned authority): opening any other QR discards it, so a draft can never
// be carried over to a different organisation or authority.
const draftKey = (suffix: string) => `qrReport:draft:${suffix}`;
const DRAFT_SUFFIXES = ['qr', 'photo', 'fix', 'form', 'stage'];
const store = {
	get: (k: string) => {
		try {
			return localStorage.getItem(k);
		} catch {
			return null;
		}
	},
	set: (k: string, v: string) => {
		try {
			localStorage.setItem(k, v);
		} catch {
			/* quota or blocked storage: the draft just won't survive a reload */
		}
	},
	remove: (k: string) => {
		try {
			localStorage.removeItem(k);
		} catch {
			/* ignore */
		}
	},
};

const isExpired = (fix: Fix | null, now = Date.now()) => !fix?.capturedAt || now - fix.capturedAt >= DRAFT_TTL_MS;

/** Signed server time + location for the photo just taken; null when the
 *  server can't be reached, in which case the photo isn't kept. */
const requestCaptureStamp = async (f: Fix): Promise<string | null> => {
	const ctrl = new AbortController();
	const timer = window.setTimeout(() => ctrl.abort(), 8000);
	try {
		const res = await fetch(`${BACKEND_URL}/api/capture-stamp`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ lat: f.lat, lng: f.lng, accuracy: f.accuracy }),
			signal: ctrl.signal,
		});
		if (!res.ok) return null;
		const data = await res.json();
		return typeof data?.stamp === 'string' ? data.stamp : null;
	} catch {
		return null;
	} finally {
		window.clearTimeout(timer);
	}
};

/** Removes the stored draft, plus anything earlier versions of this page left
 *  (per-org `qrReport:<org>:*` keys, the old `pendingQrReport`). */
const wipeAllDrafts = () => {
	try {
		Object.keys(localStorage)
			.filter((k) => k.startsWith('qrReport:') || k === 'pendingQrReport')
			.forEach((k) => localStorage.removeItem(k));
	} catch {
		/* storage blocked: nothing stored either */
	}
};

const formatAge = (ms: number) => {
	const min = Math.floor(ms / 60000);
	return min < 1 ? 'just now' : `${min} min ago`;
};

const formatCountdown = (ms: number) => {
	const s = Math.max(0, Math.ceil(ms / 1000));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const normaliseName = (s: string) =>
	s
		.toLowerCase()
		.replace(/[\s_-]+/g, ' ')
		.trim();

const dataUrlToFile = (dataUrl: string, filename: string) => {
	const [head, b64] = dataUrl.split(',');
	const mime = head.match(/:(.*?);/)?.[1] || 'image/jpeg';
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	return new File([bytes], filename, { type: mime });
};

const readMessage = async (res: Response, fallback: string) => {
	try {
		const data = await res.json();
		const msg = Array.isArray(data?.message) ? data.message[0] : data?.message;
		return typeof msg === 'string' && msg ? msg : fallback;
	} catch {
		return fallback;
	}
};

class SessionExpired extends Error {}
/** The server says the photo is past its 10 minutes (HTTP 410). */
class PhotoExpired extends Error {}

/* ── Small presentational pieces ─────────────────────────────────────────── */

const Spinner = ({ className = 'w-10 h-10' }: { className?: string }) => (
	<div className={`${className} rounded-full border-[3px] border-slate-200 border-t-[#022c56] animate-spin`} />
);

// Fixed (not sticky) so Back and Start over stay on screen while the page
// scrolls: the site's .App wrapper sets overflow-x: hidden, which turns it into
// a scroll container and silently disables position: sticky inside it. The
// spacer keeps page content from starting underneath the bar.
const TopBar = ({ title, onBack, onRestart }: { title: string; onBack?: () => void; onRestart: () => void }) => (
	<>
		<div className='fixed top-0 inset-x-0 z-20 flex items-center gap-1 px-2 h-14 bg-white border-b border-slate-100'>
			{onBack ? (
				<button
					type='button'
					onClick={onBack}
					className='w-11 h-11 rounded-full flex items-center justify-center text-slate-700 hover:bg-slate-100'
					aria-label='Back'
				>
					<i className='fa-solid fa-arrow-left text-lg'></i>
				</button>
			) : (
				<span className='w-11' />
			)}
			<span className='flex-1 min-w-0 font-semibold text-slate-900 truncate pl-1'>{title}</span>
			<StartOverButton onClick={onRestart} />
		</div>
		<div className='h-14 shrink-0' aria-hidden='true' />
	</>
);

/** Shown on every step after the first: abandons the report and returns to the QR landing page. */
const StartOverButton = ({ onClick, dark }: { onClick: () => void; dark?: boolean }) => (
	<button
		type='button'
		onClick={onClick}
		className={`shrink-0 flex items-center gap-1.5 h-9 px-[0.75rem] rounded-full text-sm font-semibold transition ${
			dark ? 'bg-black/40 text-white backdrop-blur' : 'text-[#022c56] hover:bg-slate-100'
		}`}
	>
		<i className='fa-solid fa-rotate-left text-xs'></i>
		Start over
	</button>
);

/**
 * Phone-style confirm for Start over: a bottom sheet with full-width stacked
 * buttons that grow with their text, so large system fonts and narrow screens
 * can't push the labels out (the site's shared ConfirmDialog has fixed-height
 * side-by-side buttons sized for the desktop admin panel).
 */
const StartOverSheet = ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onCancel]);

	return (
		<div
			className='fixed inset-0 z-[2100] bg-black/40 flex items-end justify-center'
			onClick={onCancel}
			role='dialog'
			aria-modal='true'
			aria-labelledby='qr-start-over-title'
		>
			<div
				className='qr-sheet w-full max-w-md bg-white rounded-t-3xl px-[1.25rem] pt-[1.25rem] text-left'
				onClick={(e) => e.stopPropagation()}
			>
				<div className='mx-auto mb-[1rem] h-1 w-10 rounded-full bg-slate-200' aria-hidden='true' />
				<h2 id='qr-start-over-title' className='text-lg font-bold text-slate-900 m-0'>
					Start over?
				</h2>
				<p className='text-sm text-slate-500 mt-1 mb-[1.25rem] leading-relaxed'>
					Your report will be cleared and you'll go back to the first page.
				</p>
				<div className='flex flex-col gap-2'>
					<button type='button' onClick={onConfirm} className='qr-btn-danger'>
						Yes, start over
					</button>
					<button type='button' onClick={onCancel} className='qr-btn-secondary'>
						Keep going
					</button>
				</div>
			</div>
		</div>
	);
};

const StoreIcon = ({ os }: { os: DeviceOS }) => (
	<i className={`fa-brands ${os === 'iOS' ? 'fa-apple' : 'fa-google-play'} text-xl`}></i>
);

const FieldLabel = ({ children, error }: { children: React.ReactNode; error?: boolean }) => (
	<span className={`block text-sm font-semibold mb-1.5 ${error ? 'text-red-600' : 'text-slate-800'}`}>{children}</span>
);

const PreviewRow = ({ label, value }: { label: string; value: string }) => (
	<div className='px-[1rem] py-[0.75rem]'>
		<span className='block text-xs font-semibold uppercase tracking-wide text-slate-400'>{label}</span>
		<span className='block text-[15px] text-slate-900 mt-0.5 whitespace-pre-wrap break-words'>{value || '—'}</span>
	</div>
);

const fieldClass = (error?: boolean) =>
	`w-full px-[1rem] py-[0.75rem] rounded-xl border-[1px] bg-white text-base text-slate-900 outline-none transition focus:border-[#022c56] focus:ring-2 focus:ring-[#022c56]/10 ${
		error ? 'border-red-400' : 'border-slate-200'
	}`;

const OptionChips = ({
	options,
	value,
	onChange,
	error,
}: {
	options: string[];
	value: string;
	onChange: (v: string) => void;
	error?: boolean;
}) => (
	<div className={`flex flex-wrap gap-2 ${error ? 'p-2 rounded-xl ring-1 ring-red-300' : ''}`}>
		{options.map((o) => (
			<button
				key={o}
				type='button'
				onClick={() => onChange(o)}
				className={`px-3.5 py-2 rounded-full border-[1px] text-sm transition ${
					value === o
						? 'bg-[#022c56] border-[#022c56] text-white'
						: 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
				}`}
			>
				{o}
			</button>
		))}
	</div>
);

/* ── QR scan analytics ───────────────────────────────────────────────────── */

// A printed code from the admin panel carries its id as `?qr=<id>` (see
// pages/QRCodes/qr.ts). The id is counted as a scan here and sent with the
// report so the admin panel can show both per code. Codes without it (older
// links, hand-typed URLs) work exactly as before, just uncounted.
const parseQrCodeId = (search: string): number | null => {
	const n = Number(new URLSearchParams(search).get('qr'));
	return Number.isInteger(n) && n > 0 ? n : null;
};

const scanKey = (qrCodeId: number) => `qrReport:scanned:${qrCodeId}`;

/**
 * Should this page load count as a new scan of `qrCodeId`? One scan per
 * browser session: the trip to /login and back, a reload, or the draft
 * restore all land here again and must not inflate the count, while a fresh
 * scan later (new tab from the camera app) counts again.
 */
const shouldRecordScan = (qrCodeId: number): boolean => {
	try {
		if (sessionStorage.getItem(scanKey(qrCodeId))) return false;
		sessionStorage.setItem(scanKey(qrCodeId), '1');
		return true;
	} catch {
		// Storage blocked: count it rather than lose every scan from this browser.
		return true;
	}
};

/** Fire-and-forget; a failed beacon must never surface on the page. */
const recordQrScan = (qrCodeId: number) => {
	if (!shouldRecordScan(qrCodeId)) return;
	fetch(`${BACKEND_URL}/api/qr/${qrCodeId}/scan`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ visitorId: getVisitorId() }),
		keepalive: true,
	}).catch(() => {
		/* best-effort */
	});
};

/* ── Page ────────────────────────────────────────────────────────────────── */

function QRReport() {
	const { orgName = '', authorityName } = useParams<{ orgName: string; authorityName?: string }>();
	const { user, set_user, setIsLoggedIn, isAuthLoading } = useUser();
	const { clearData } = useData();
	const [confirmingStartOver, setConfirmingStartOver] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const os = useMemo(detectOS, []);
	const qrCodeId = useMemo(() => parseQrCodeId(location.search), [location.search]);

	const [org, setOrg] = useState<QrOrg | null>(null);
	const [orgError, setOrgError] = useState<'notfound' | 'network' | null>(null);
	const [step, setStep] = useState<Step>('LANDING');
	const [problem, setProblem] = useState<PermissionProblem | null>(null);

	const [photo, setPhoto] = useState('');
	const [fix, setFix] = useState<Fix | null>(null);
	const [facing, setFacing] = useState<'environment' | 'user'>('environment');
	const [cameraReady, setCameraReady] = useState(false);
	const [accuracyGraceOver, setAccuracyGraceOver] = useState(false);
	const [locationLost, setLocationLost] = useState(false);
	const [acquiredPill, setAcquiredPill] = useState(false);
	const [online, setOnline] = useState(() => navigator.onLine !== false);
	const [capturing, setCapturing] = useState(false);
	const [captureError, setCaptureError] = useState<string | null>(null);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [now, setNow] = useState(() => Date.now());
	const [expiredNotice, setExpiredNotice] = useState(false);

	const [outsideBy, setOutsideBy] = useState<number | null>(null);
	const [checkingArea, setCheckingArea] = useState(false);

	const [authorityId, setAuthorityId] = useState<number | null>(null);
	const [title, setTitle] = useState('');
	const [body, setBody] = useState('');
	const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<Set<string>>(new Set());
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [reportId, setReportId] = useState<number | null>(null);

	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const watchIdRef = useRef<number | null>(null);
	const fixRef = useRef<Fix | null>(null);
	const graceTimerRef = useRef<number | null>(null);
	const lastFixAtRef = useRef(0);
	const probeTimerRef = useRef<number | null>(null);
	// When the in-flight location check started (0 = none).
	const probingRef = useRef(0);
	const geofenceRef = useRef<Promise<{ inside: boolean; distanceMeters: number }> | null>(null);
	const mediaIdRef = useRef<number | null>(null);

	/* ── Organisation + QR-pinned authority ── */

	useEffect(() => {
		let cancelled = false;
		setOrg(null);
		setOrgError(null);
		fetch(`${BACKEND_URL}/api/organisations/${encodeURIComponent(orgName)}/authorities`)
			.then(async (res) => {
				if (cancelled) return;
				if (res.status === 404) return setOrgError('notfound');
				if (!res.ok) return setOrgError('network');
				setOrg(await res.json());
			})
			.catch(() => !cancelled && setOrgError('network'));
		return () => {
			cancelled = true;
		};
	}, [orgName]);

	// Count the scan once the page is known to be real (org found).
	useEffect(() => {
		if (org && qrCodeId) recordQrScan(qrCodeId);
	}, [org, qrCodeId]);

	// A QR printed for one authority (…/org/IIT%20Delhi/Sanitation) pins it.
	// If the name no longer matches (renamed or removed), fall back to letting
	// the user choose rather than dead-ending a printed QR.
	const lockedAuthority = useMemo(() => {
		if (!org || !authorityName) return null;
		const wanted = normaliseName(authorityName);
		return org.authorities.find((a) => normaliseName(a.name) === wanted) || null;
	}, [org, authorityName]);

	const authority = useMemo(
		() => lockedAuthority || org?.authorities.find((a) => a.id === authorityId) || null,
		[lockedAuthority, org, authorityId]
	);
	const visibleFields = useMemo(
		() => (authority?.requiredFields || []).filter((f) => !isAutoInjectedField(f)),
		[authority]
	);

	// Prefill the entry number the account last reported with. Only a field
	// the user hasn't touched yet (undefined) is filled, so a restored draft or
	// their own edit, even clearing it, always wins.
	const savedEntryNumber = user?.entryNumber || '';
	useEffect(() => {
		if (!savedEntryNumber) return;
		const untouched = visibleFields.filter((f) => isEntryNumberField(f) && fieldValues[f] === undefined);
		if (untouched.length === 0) return;
		setFieldValues((prev) => {
			const next = { ...prev };
			untouched.forEach((f) => (next[f] = savedEntryNumber));
			return next;
		});
	}, [visibleFields, savedEntryNumber, fieldValues]);

	/* ── Draft restore / persist ── */

	// Which QR this page was opened from; the stored draft must match it.
	const qrId = org ? `${org.key}|${lockedAuthority?.id ?? ''}` : null;
	// Another tab may have taken the slot over since this one loaded; only the
	// owner writes to or clears it.
	const ownsDraft = () => !!qrId && store.get(draftKey('qr')) === qrId;

	// Reopening the same QR (or coming back from /login or /signup) restores
	// its draft; a draft that had reached the confirm step reopens on the
	// preview once the user is logged in. Any other QR's draft is discarded.
	const restoredRef = useRef(false);
	useEffect(() => {
		if (!org || isAuthLoading || restoredRef.current) return;
		restoredRef.current = true;
		if (!ownsDraft()) {
			wipeAllDrafts();
			return;
		}
		const savedPhoto = store.get(draftKey('photo'));
		const savedFix = store.get(draftKey('fix'));
		try {
			if (!savedPhoto || !savedFix) return;
			const savedFixObj: Fix = JSON.parse(savedFix);
			if (!savedFixObj.stamp) {
				clearDraft(); // from before capture stamps: can't be sent any more
				return;
			}
			if (isExpired(savedFixObj)) {
				clearDraft();
				setExpiredNotice(true);
				return;
			}
			const draft: Draft = JSON.parse(store.get(draftKey('form')) || '{}');
			setPhoto(savedPhoto);
			setFix(savedFixObj);
			setNow(Date.now());
			setAuthorityId(draft.authorityId ?? null);
			setTitle(draft.title || '');
			setBody(draft.body || '');
			setFieldValues(draft.fields || {});
			setStep(user && store.get(draftKey('stage')) === 'preview' ? 'PREVIEW' : 'DETAILS');
		} catch {
			clearDraft(); // corrupt draft: start fresh
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [org, isAuthLoading, user]);

	useEffect(() => {
		if (!photo || !fix || !ownsDraft()) return;
		const draft: Draft = { authorityId, title, body, fields: fieldValues };
		store.set(draftKey('form'), JSON.stringify(draft));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qrId, photo, fix, authorityId, title, body, fieldValues]);

	const clearDraft = () => {
		if (!ownsDraft()) return;
		DRAFT_SUFFIXES.forEach((s) => store.remove(draftKey(s)));
	};

	// Every step is a new screen: close the phone keyboard (nothing is focused
	// on arrival) and start at the top so the user sees what the step is.
	useEffect(() => {
		(document.activeElement as HTMLElement | null)?.blur?.();
		// Body and .App can both be the scroller on this site, so reset all of them.
		window.scrollTo(0, 0);
		document.body.scrollTop = 0;
		document.querySelector('.App')?.scrollTo?.(0, 0);
	}, [step]);

	// Remember which of the two form screens the draft is on, for a reload.
	useEffect(() => {
		if ((step === 'DETAILS' || step === 'PREVIEW') && ownsDraft()) {
			store.set(draftKey('stage'), step === 'PREVIEW' ? 'preview' : 'details');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qrId, step]);

	/* ── Camera + location ── */

	const stopCamera = useCallback(() => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
		setCameraReady(false);
	}, []);

	const stopLocation = useCallback(() => {
		if (watchIdRef.current != null) navigator.geolocation?.clearWatch(watchIdRef.current);
		watchIdRef.current = null;
		if (graceTimerRef.current != null) window.clearTimeout(graceTimerRef.current);
		graceTimerRef.current = null;
		if (probeTimerRef.current != null) window.clearInterval(probeTimerRef.current);
		probeTimerRef.current = null;
		probingRef.current = 0;
	}, []);

	useEffect(
		() => () => {
			stopCamera();
			stopLocation();
		},
		[stopCamera, stopLocation]
	);

	// A photo is only taken online: it needs the server's capture stamp.
	useEffect(() => {
		const update = () => setOnline(navigator.onLine !== false);
		window.addEventListener('online', update);
		window.addEventListener('offline', update);
		return () => {
			window.removeEventListener('online', update);
			window.removeEventListener('offline', update);
		};
	}, []);

	const showProblem = useCallback(
		(p: PermissionProblem) => {
			stopCamera();
			stopLocation();
			setProblem(p);
			setStep('PERMISSION');
		},
		[stopCamera, stopLocation]
	);

	const openCamera = useCallback(
		async (mode: 'environment' | 'user') => {
			stopCamera();
			if (!navigator.mediaDevices?.getUserMedia) return showProblem({ kind: 'camera', reason: 'unsupported' });
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
					audio: false,
				});
				streamRef.current = stream;
				if (videoRef.current) videoRef.current.srcObject = stream;
				setCameraReady(true);
			} catch (err) {
				showProblem({ kind: 'camera', reason: cameraProblemFrom(err) });
			}
		},
		[stopCamera, showProblem]
	);

	const startCapture = useCallback(() => {
		setProblem(null);
		setPhoto('');
		mediaIdRef.current = null;
		setStep('CAMERA');

		// Location is mandatory, so it is watched for the whole time the camera
		// is open and the freshest fix is stamped on the photo at capture.
		if (!navigator.geolocation || !window.isSecureContext) {
			return showProblem({ kind: 'location', reason: 'unsupported' });
		}
		stopLocation();
		fixRef.current = null;
		lastFixAtRef.current = 0;
		setFix(null);
		setAccuracyGraceOver(false);
		setLocationLost(false);
		setExpiredNotice(false);
		setCaptureError(null);

		const onFix = (pos: GeolocationPosition) => {
			const f = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
			// The first fix starts the grace period: past it, the shutter
			// stops waiting for GOOD_ACCURACY_M and takes the best fix.
			if (!fixRef.current) {
				graceTimerRef.current = window.setTimeout(() => setAccuracyGraceOver(true), ACCURACY_GRACE_MS);
			}
			fixRef.current = f;
			lastFixAtRef.current = Date.now();
			setFix(f);
			setLocationLost(false);
		};
		const onError = (err: GeolocationPositionError) => {
			// Before the first fix, or when permission is taken away: show how
			// to turn location on.
			if (!fixRef.current || err.code === err.PERMISSION_DENIED) {
				showProblem({ kind: 'location', reason: locationProblemFrom(err) });
				return;
			}
			// Location switched off (or lost) after a fix: block the shutter
			// until a fresh reading comes back. A timeout alone isn't enough to
			// say so; the staleness check below covers a watch gone quiet.
			if (err.code === err.POSITION_UNAVAILABLE) setLocationLost(true);
		};
		// maximumAge 0: only fresh readings, so the fix really is where the photo is taken.
		const options = { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 };
		const watch = () => {
			if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
			watchIdRef.current = navigator.geolocation.watchPosition(onFix, onError, options);
		};
		watch();

		// Keep probing: a phone standing still may not report through the watch,
		// and some browsers end the watch after an error, so ask for a fresh
		// reading every few seconds and re-arm the watch while location is lost.
		probeTimerRef.current = window.setInterval(() => {
			if (!fixRef.current) return; // still finding the first fix
			const stale = Date.now() - lastFixAtRef.current > LOCATION_STALE_MS;
			if (stale) {
				setLocationLost(true);
				watch();
			}
			// One check at a time; a browser that never answers is given up on.
			if (probingRef.current && Date.now() - probingRef.current < 15000) return;
			probingRef.current = Date.now();
			navigator.geolocation.getCurrentPosition(
				(pos) => {
					probingRef.current = 0;
					onFix(pos);
				},
				(err) => {
					probingRef.current = 0;
					onError(err);
				},
				{ enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
			);
		}, LOCATION_PROBE_MS);
		openCamera(facing);
	}, [facing, openCamera, showProblem, stopLocation]);

	// Re-attach the stream if the <video> mounted after getUserMedia resolved.
	useEffect(() => {
		if (
			step === 'CAMERA' &&
			videoRef.current &&
			streamRef.current &&
			videoRef.current.srcObject !== streamRef.current
		) {
			videoRef.current.srcObject = streamRef.current;
		}
	}, [step, cameraReady]);

	const flipCamera = () => {
		const next = facing === 'environment' ? 'user' : 'environment';
		setFacing(next);
		openCamera(next);
	};

	const checkGeofence = (key: string, f: Fix) => {
		const ctrl = new AbortController();
		const timer = window.setTimeout(() => ctrl.abort(), 6000);
		return fetch(`${BACKEND_URL}/api/organisations/${encodeURIComponent(key)}/geofence?lat=${f.lat}&lng=${f.lng}`, {
			signal: ctrl.signal,
		})
			.then((r) => (r.ok ? r.json() : { inside: true, distanceMeters: 0 }))
			.catch(() => ({ inside: true, distanceMeters: 0 })) // fail open, the server re-checks on submit
			.finally(() => window.clearTimeout(timer));
	};

	const capture = async () => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (capturing || !fixRef.current || !video || !canvas || !video.videoWidth) return;
		// Never stamp a photo with a location that is no longer current.
		if (locationLost || Date.now() - lastFixAtRef.current > LOCATION_STALE_MS) {
			setLocationLost(true);
			return;
		}
		const at: Fix = { ...fixRef.current };

		const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(video.videoWidth, video.videoHeight));
		canvas.width = Math.round(video.videoWidth * scale);
		canvas.height = Math.round(video.videoHeight * scale);
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		if (facing === 'user') {
			ctx.translate(canvas.width, 0);
			ctx.scale(-1, 1);
		}
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		const shot = canvas.toDataURL('image/jpeg', 0.8);

		// The photo is kept only once the server has stamped it; the camera
		// stays open meanwhile, so a failure just means "take it again".
		setCaptureError(null);
		setCapturing(true);
		const stamp = await requestCaptureStamp(at);
		setCapturing(false);
		if (!stamp) {
			setCaptureError('No internet connection. Connect and take the photo again.');
			return;
		}
		const f: Fix = { ...at, capturedAt: Date.now(), stamp };

		setPhoto(shot);
		setFix(f);
		setNow(f.capturedAt!);
		stopCamera();
		stopLocation();
		if (org) geofenceRef.current = checkGeofence(org.key, f);
		setStep('REVIEW');
	};

	const confirmPhoto = async () => {
		if (!org || !fix) return;
		setCheckingArea(true);
		const verdict = await (geofenceRef.current || checkGeofence(org.key, fix));
		setCheckingArea(false);
		if (!verdict.inside) {
			setOutsideBy(verdict.distanceMeters);
			setStep('OUTSIDE');
			return;
		}
		// A confirmed photo starts this QR's draft and replaces any other.
		wipeAllDrafts();
		store.set(draftKey('qr'), qrId!);
		store.set(draftKey('photo'), photo);
		store.set(draftKey('fix'), JSON.stringify(fix));
		const draft: Draft = { authorityId, title, body, fields: fieldValues }; // kept across a retake
		store.set(draftKey('form'), JSON.stringify(draft));
		setSubmitError(null);
		setStep('DETAILS');
	};

	/* ── Details + submit ── */

	const validate = () => {
		const missing = new Set<string>();
		if (!authority) missing.add('authority');
		if (!title.trim()) missing.add('title');
		if (!body.trim()) missing.add('body');
		visibleFields.forEach((f) => !(fieldValues[f] || '').trim() && missing.add(f));
		setErrors(missing);
		return missing.size === 0;
	};

	const submit = async (asUser: User) => {
		if (!authority || !fix) return;
		setStep('SUBMITTING');
		setSubmitError(null);
		try {
			if (mediaIdRef.current == null) {
				const form = new FormData();
				form.append('file', dataUrlToFile(photo, 'report.jpg'));
				form.append('latitude', String(fix.lat));
				form.append('longitude', String(fix.lng));
				form.append('purpose', 'report');
				const mediaRes = await fetch(`${BACKEND_URL}/api/createMedia`, {
					method: 'POST',
					credentials: 'include',
					body: form,
				});
				if (mediaRes.status === 401) throw new SessionExpired();
				if (!mediaRes.ok) throw new Error(await readMessage(mediaRes, 'Could not upload the photo. Please try again.'));
				mediaIdRef.current = Number(await mediaRes.json());
			}

			const reportRes = await fetch(`${BACKEND_URL}/api/reportAuthority`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					authorityId: authority.id,
					qrCodeId,
					title: title.trim(),
					body: body.trim(),
					isAnonymous: false,
					mediaIds: [mediaIdRef.current],
					captureStamps: fix.stamp ? [fix.stamp] : [],
					requiredFieldValues: buildRequiredFieldValues(
						authority.requiredFields,
						fieldValues,
						asUser.phoneNumber ? String(asUser.phoneNumber) : '',
						authority.name
					),
				}),
			});
			if (reportRes.status === 401) throw new SessionExpired();
			if (reportRes.status === 410) throw new PhotoExpired();
			if (!reportRes.ok) throw new Error(await readMessage(reportRes, 'Could not send the report. Please try again.'));

			setReportId(Number(await reportRes.json()) || null);
			// The backend just saved the submitted entry number on the account;
			// mirror it so a next report in this session prefills the same value.
			const entryField = visibleFields.find(isEntryNumberField);
			const savedEntry = entryField ? normaliseEntryNumber(fieldValues[entryField] || '') : '';
			if (savedEntry) set_user((u) => (u ? { ...u, entryNumber: savedEntry } : u));
			clearDraft();
			setStep('SUCCESS');
		} catch (err: any) {
			if (err instanceof PhotoExpired) {
				startOver();
				setExpiredNotice(true);
				return;
			}
			if (err instanceof SessionExpired) {
				localStorage.removeItem('isAuthed');
				set_user(null);
				setIsLoggedIn(false);
				goToLogin();
				return;
			}
			setSubmitError(err?.message || 'Could not send the report. Please try again.');
			setStep('PREVIEW');
		}
	};

	// The site's own login page; it (and the signup page it links to) sends the
	// user back to location.state.from once they're in.
	const goToLogin = () => {
		if (ownsDraft()) store.set(draftKey('stage'), 'preview');
		// Keep ?qr= so the report filed after login is still credited to the code.
		navigate('/login', { state: { from: location.pathname + location.search } });
	};

	const onContinue = () => {
		if (!validate()) {
			setSubmitError(null);
			document.querySelector('[data-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			return;
		}
		setSubmitError(null);
		if (user) setStep('PREVIEW');
		else goToLogin();
	};

	const switchAccount = async () => {
		try {
			await fetch(`${BACKEND_URL}/auth/logout`, { method: 'GET', credentials: 'include' });
		} catch {
			/* the local sign-out below still applies */
		}
		localStorage.removeItem('isAuthed');
		clearData();
		set_user(null);
		setIsLoggedIn(false);
	};

	const startOver = () => {
		stopCamera();
		stopLocation();
		clearDraft();
		mediaIdRef.current = null;
		geofenceRef.current = null;
		setPhoto('');
		setFix(null);
		setProblem(null);
		setOutsideBy(null);
		setAuthorityId(null);
		setTitle('');
		setBody('');
		setFieldValues({});
		setErrors(new Set());
		setSubmitError(null);
		setViewerOpen(false);
		setConfirmingStartOver(false);
		setExpiredNotice(false);
		setCaptureError(null);
		setLocationLost(false);
		setReportId(null);
		setStep('LANDING');
	};

	// Every Start over button asks first, on every step.
	const requestStartOver = () => setConfirmingStartOver(true);

	// "GPS acquired" pill: up for a moment each time location becomes usable.
	const locationUsable =
		step === 'CAMERA' && !!fix && !locationLost && (fix.accuracy <= GOOD_ACCURACY_M || accuracyGraceOver);
	useEffect(() => {
		if (!locationUsable) {
			setAcquiredPill(false);
			return;
		}
		setAcquiredPill(true);
		const id = window.setTimeout(() => setAcquiredPill(false), ACQUIRED_PILL_MS);
		return () => window.clearTimeout(id);
	}, [locationUsable]);

	// Ten-minute window from taking the photo. Ticks every second while a
	// draft is on screen (drives the countdown) and resets the page when it
	// runs out. Not while SUBMITTING: a send already under way may finish.
	const startOverRef = useRef(startOver);
	startOverRef.current = startOver;
	const capturedAt = fix?.capturedAt;
	const draftOnScreen = !!capturedAt && DRAFT_STEPS.includes(step);
	useEffect(() => {
		if (!draftOnScreen || !capturedAt) return;
		const tick = () => {
			const t = Date.now();
			setNow(t);
			if (t - capturedAt >= DRAFT_TTL_MS) {
				startOverRef.current();
				setExpiredNotice(true);
			}
		};
		tick();
		const id = window.setInterval(tick, 1000);
		return () => window.clearInterval(id);
	}, [draftOnScreen, capturedAt]);

	const clearError = (k: string) => {
		if (!errors.has(k)) return;
		setErrors((prev) => {
			const next = new Set(prev);
			next.delete(k);
			return next;
		});
	};

	/* ── Render ── */

	// Each step returns its own full-screen layout; the start-over confirm
	// dialog is mounted alongside whichever one is showing.
	const renderStep = () => {
		if (orgError) {
			return (
				<div className='qr-shell items-center justify-center px-6 text-center'>
					<img src={BReporterLogo} alt='B-Reporter' className='w-16 h-16 object-contain mb-[1rem]' />
					<h1 className='text-xl font-bold text-slate-900 m-0'>
						{orgError === 'notfound' ? 'This QR code is not active' : 'Could not load this page'}
					</h1>
					<p className='text-sm text-slate-500 mt-2 mb-6 max-w-xs'>
						{orgError === 'notfound'
							? 'You can still report the problem from the B-Reporter app.'
							: 'Check your internet connection and try again.'}
					</p>
					{orgError === 'network' ? (
						<button type='button' onClick={() => window.location.reload()} className='qr-btn-primary max-w-xs'>
							Try again
						</button>
					) : (
						<button type='button' onClick={() => openAppOrStore(os)} className='qr-btn-primary max-w-xs'>
							<StoreIcon os={os} /> Get the app
						</button>
					)}
				</div>
			);
		}

		if (!org) {
			return (
				<div className='qr-shell items-center justify-center'>
					<Spinner />
				</div>
			);
		}

		const orgLogo = org.logoUrl ? `${BACKEND_URL}${org.logoUrl}` : null;

		/* LANDING — must fit one phone screen without scrolling. */
		if (step === 'LANDING') {
			return (
				<div className='qr-shell'>
					<div className='flex-1 flex flex-col w-full max-w-md mx-auto px-[1.25rem] py-6 min-h-0'>
						<div className='flex flex-col items-center pt-2'>
							<img src={BReporterLogo} alt='B-Reporter' className='w-[72px] h-[72px] object-contain' />
							<span className='text-sm font-semibold tracking-wide text-[#022c56] mt-1'>B-Reporter</span>
						</div>

						<div className='flex-1 flex flex-col justify-center min-h-0'>
							<div className='bg-white rounded-2xl border-[1px] border-slate-100 shadow-sm px-[1.25rem] py-6 text-center'>
								{orgLogo && <img src={orgLogo} alt='' className='w-14 h-14 object-contain mx-auto mb-[0.75rem]' />}
								<p className='text-sm text-slate-500 m-0'>Report a problem at</p>
								<h1 className='text-xl font-bold text-slate-900 leading-snug mt-1 mb-0'>{org.fullName}</h1>
								{lockedAuthority && (
									<span className='inline-flex items-center gap-1.5 mt-[0.75rem] px-[0.75rem] py-1 rounded-full bg-blue-50 text-[#022c56] text-sm font-medium'>
										<i className='fa-solid fa-building-user text-xs'></i>
										{lockedAuthority.name}
									</span>
								)}
							</div>
							{expiredNotice && (
								<p className='mt-[0.75rem] mb-0 text-sm text-amber-800 bg-amber-50 rounded-xl px-[0.75rem] py-2 text-center'>
									Reports must be sent within 10 minutes of taking the photo, so the last one was cleared.
								</p>
							)}
						</div>

						<div className='flex flex-col gap-[0.75rem] pt-6'>
							<button type='button' onClick={() => openAppOrStore(os)} className='qr-btn-primary relative'>
								{/* Icon pinned left so the two lines of text centre on the button itself. */}
								<span className='absolute left-[1.25rem] top-1/2 -translate-y-1/2'>
									<StoreIcon os={os} />
								</span>
								<span className='flex flex-col items-center text-center leading-tight px-10'>
									<span>Report on App</span>
									<span className='text-xs font-normal text-white/70'>Recommended</span>
								</span>
							</button>
							<button type='button' onClick={startCapture} className='qr-btn-secondary'>
								Continue in browser
							</button>
						</div>
					</div>
				</div>
			);
		}

		if (step === 'CAMERA') {
			const locationReady = locationUsable;
			const canCapture = cameraReady && locationReady && online && !capturing;
			// Status pill over the bottom of the camera view. "GPS acquired"
			// fades away after a moment; every other state stays up.
			const pill = !online
				? { text: 'No internet connection', icon: 'fa-wifi', tone: 'bg-red-500 text-white' }
				: locationLost
					? {
							text: 'Location is off. Turn it on to take a photo',
							icon: 'fa-location-crosshairs',
							tone: 'bg-red-500 text-white',
						}
					: locationReady
						? { text: 'GPS acquired', icon: 'fa-location-dot', tone: 'bg-emerald-500 text-white' }
						: { text: 'Finding your location…', icon: 'fa-location-dot', tone: 'bg-black/55 text-white' };
			const pillHidden = online && !locationLost && locationReady && !acquiredPill;
			return (
				<div className='qr-shell bg-black'>
					<div className='relative flex-1 min-h-0 overflow-hidden'>
						<video
							ref={videoRef}
							autoPlay
							playsInline
							muted
							className={`absolute inset-0 w-full h-full object-cover ${facing === 'user' ? 'scale-x-[-1]' : ''}`}
						/>
						{!cameraReady && (
							<div className='absolute inset-0 flex items-center justify-center'>
								<Spinner className='w-10 h-10 border-white/20 border-t-white' />
							</div>
						)}
						<div className='absolute top-0 inset-x-0 flex items-center justify-between gap-2 p-[0.75rem]'>
							<StartOverButton onClick={requestStartOver} dark />
							<button
								type='button'
								onClick={flipCamera}
								className='w-11 h-11 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur'
								aria-label='Switch camera'
							>
								<i className='fa-solid fa-camera-rotate text-lg'></i>
							</button>
						</div>
						<div className='absolute bottom-[1rem] inset-x-0 flex justify-center px-[1rem] pointer-events-none'>
							<span
								className={`px-[0.75rem] py-1.5 rounded-full text-xs font-semibold backdrop-blur shadow ${pill.tone}`}
								style={{
									opacity: pillHidden ? 0 : 1,
									transform: pillHidden ? 'translateY(12px)' : 'translateY(0)',
									transition: 'opacity 400ms ease, transform 400ms ease',
								}}
								aria-live='polite'
							>
								<i className={`fa-solid ${pill.icon} mr-1.5`}></i>
								{pill.text}
							</span>
						</div>
					</div>
					<div className='shrink-0 flex flex-col items-center gap-[0.75rem] pt-[1rem] pb-[1.25rem] bg-black'>
						<button
							type='button'
							onClick={capture}
							disabled={!canCapture}
							className='w-[76px] h-[76px] rounded-full border-4 border-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition'
							aria-label='Take photo'
						>
							<span className='w-[60px] h-[60px] rounded-full bg-white' />
						</button>
						<span className={`text-xs text-center px-[1rem] ${captureError ? 'text-red-300' : 'text-white/70'}`}>
							{capturing
								? 'Saving photo…'
								: captureError
									? captureError
									: !online
										? 'Photos can only be taken while online'
										: locationLost
											? 'Turn location back on to take a photo'
											: canCapture
												? 'Take a photo of the problem'
												: 'Getting your location first…'}
						</span>
					</div>
					<canvas ref={canvasRef} className='hidden' />
				</div>
			);
		}

		if (step === 'PERMISSION' && problem) {
			return (
				<div className='qr-page'>
					<TopBar title='Report a problem' onBack={() => setStep('LANDING')} onRestart={requestStartOver} />
					<div className='w-full max-w-md mx-auto px-[1.25rem] py-8'>
						<PermissionHelp problem={problem} os={os} onRetry={startCapture} />
					</div>
				</div>
			);
		}

		if (step === 'REVIEW') {
			return (
				<div className='qr-shell bg-black'>
					<div className='relative flex-1 min-h-0 flex items-center justify-center'>
						<img src={photo} alt='Reported problem' className='max-w-full max-h-full object-contain' />
						<div className='absolute top-0 left-0 p-[0.75rem]'>
							<StartOverButton onClick={requestStartOver} dark />
						</div>
					</div>
					<div className='shrink-0 grid grid-cols-2 gap-[0.75rem] p-[1rem] bg-black'>
						<button
							type='button'
							onClick={startCapture}
							className='py-3.5 rounded-xl bg-white/10 text-white font-semibold'
						>
							Retake
						</button>
						<button
							type='button'
							onClick={confirmPhoto}
							disabled={checkingArea}
							className='py-3.5 rounded-xl bg-white text-[#022c56] font-semibold disabled:opacity-70'
						>
							{checkingArea ? 'Checking…' : 'Confirm'}
						</button>
					</div>
				</div>
			);
		}

		if (step === 'OUTSIDE') {
			const away =
				outsideBy == null
					? ''
					: outsideBy >= 1000
						? `${(outsideBy / 1000).toFixed(1)} km`
						: `${Math.round(outsideBy)} m`;
			return (
				<div className='qr-page'>
					<TopBar title='Report a problem' onBack={() => setStep('REVIEW')} onRestart={requestStartOver} />
					<div>
						<div className='max-w-md mx-auto px-[1.25rem] py-10 flex flex-col items-center text-center'>
							<div className='w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-[1rem]'>
								<i className='fa-solid fa-map-location-dot text-2xl'></i>
							</div>
							<h2 className='text-xl font-bold text-slate-900 m-0'>You seem to be outside {org.fullName}</h2>
							<p className='text-sm text-slate-500 mt-2 mb-6'>
								Reports here can only be sent from inside the campus{away ? `. You are about ${away} away.` : '.'} If
								you are inside, step outdoors for a better location and try again.
							</p>
							<button type='button' onClick={startCapture} className='qr-btn-primary'>
								Take photo again
							</button>
						</div>
					</div>
				</div>
			);
		}

		if (step === 'SUBMITTING') {
			return (
				<div className='qr-shell items-center justify-center text-center px-6'>
					<Spinner className='w-14 h-14' />
					<h2 className='text-lg font-bold text-slate-900 mt-[1.25rem] mb-1'>Sending your report…</h2>
					<p className='text-sm text-slate-500 m-0'>Please keep this page open.</p>
				</div>
			);
		}

		if (step === 'SUCCESS') {
			return (
				<div className='qr-page'>
					<div>
						<div className='max-w-md mx-auto px-[1.25rem] py-8 flex flex-col items-center text-center'>
							<div className='w-20 h-20 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center'>
								<i className='fa-solid fa-check text-4xl'></i>
							</div>
							<h1 className='text-2xl font-bold text-slate-900 mt-[1rem] mb-1'>Report sent</h1>
							<p className='text-sm text-slate-500 m-0'>
								{authority?.name} at {org.fullName} has received it.
							</p>

							<div className='w-full mt-6 flex gap-[0.75rem] items-center text-left bg-white rounded-2xl border-[1px] border-slate-100 shadow-sm p-[0.75rem]'>
								<img src={photo} alt='' className='w-16 h-16 rounded-xl object-cover shrink-0' />
								<div className='min-w-0'>
									<p className='font-semibold text-slate-900 truncate m-0'>{title}</p>
									<p className='text-xs text-slate-500 mt-1 mb-0'>
										{reportId ? `Report #${reportId} · ` : ''}
										{authority?.name}
									</p>
								</div>
							</div>

							<div className='w-full mt-[1rem] rounded-2xl bg-[#022c56] text-white p-[1.25rem] text-left'>
								<p className='font-semibold text-lg m-0'>Track your report on the app</p>
								<p className='text-sm text-white/75 mt-1 mb-[1rem]'>
									See its status and get notified when it is fixed. Log in with the same phone number.
								</p>
								<button
									type='button'
									onClick={() => openAppOrStore(os)}
									className='w-full flex items-center justify-center gap-[0.75rem] py-3.5 rounded-xl bg-white text-[#022c56] font-semibold'
								>
									<StoreIcon os={os} />
									{os === 'iOS' ? 'Get it on the App Store' : 'Get it on Google Play'}
								</button>
							</div>

							<button type='button' onClick={startOver} className='mt-[1.25rem] text-sm font-semibold text-[#022c56]'>
								Report another problem
							</button>
						</div>
					</div>
				</div>
			);
		}

		const msLeft = capturedAt ? DRAFT_TTL_MS - (now - capturedAt) : 0;
		const photoBlock = (
			<button
				type='button'
				onClick={() => setViewerOpen(true)}
				className='relative w-full h-[38vh] min-h-[200px] rounded-2xl overflow-hidden bg-slate-900'
				aria-label='Open photo'
			>
				<img src={photo} alt='Reported problem' className='w-full h-full object-cover' />
				{capturedAt && (
					<span
						className={`absolute top-2 left-2 px-2.5 py-1 rounded-full text-xs font-semibold text-white ${
							msLeft <= 2 * 60 * 1000 ? 'bg-amber-500' : 'bg-black/60'
						}`}
					>
						<i className='fa-regular fa-clock mr-1.5'></i>
						Taken {formatAge(now - capturedAt)} · {formatCountdown(msLeft)} left to send
					</span>
				)}
				<span className='absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center'>
					<i className='fa-solid fa-expand'></i>
				</span>
			</button>
		);

		const errorBanner = submitError && (
			<div
				className='flex gap-2 items-start text-sm text-red-700 bg-red-50 rounded-xl px-[0.75rem] py-2.5'
				role='alert'
			>
				<i className='fa-solid fa-circle-exclamation mt-0.5'></i>
				<span>{submitError}</span>
			</div>
		);

		const accountLine = user && (
			<p className='text-xs text-slate-500 text-center mt-0 mb-2'>
				Sending as <b className='text-slate-700'>{user.name}</b> ·{' '}
				<button type='button' onClick={switchAccount} className='font-semibold text-[#022c56]'>
					Not you?
				</button>
			</p>
		);

		const viewer = viewerOpen && <PhotoViewer src={photo} onClose={() => setViewerOpen(false)} />;

		/* PREVIEW — the final report exactly as it will be sent, confirmed by the user. */
		if (step === 'PREVIEW' && authority) {
			const sentFields = buildRequiredFieldValues(
				authority.requiredFields,
				fieldValues,
				user?.phoneNumber ? String(user.phoneNumber) : '',
				authority.name
			);
			return (
				<div className='qr-page'>
					<TopBar title='Check your report' onBack={() => setStep('DETAILS')} onRestart={requestStartOver} />
					<div className='w-full max-w-md mx-auto px-[1rem] pt-[1rem] pb-8 flex flex-col gap-[1rem]'>
						{photoBlock}
						{errorBanner}
						<div className='bg-white rounded-2xl border-[1px] border-slate-100 shadow-sm divide-y divide-slate-100'>
							<PreviewRow label='Sending to' value={`${authority.name}, ${org.fullName}`} />
							<PreviewRow label='Problem' value={title.trim()} />
							<PreviewRow label='Details' value={body.trim()} />
							{Object.entries(sentFields).map(([k, v]) => (
								<PreviewRow key={k} label={prettyFieldLabel(k)} value={isAutoInjectedField(k) ? `+${v}` : v} />
							))}
						</div>
						<button
							type='button'
							onClick={() => setStep('DETAILS')}
							className='self-start flex items-center gap-1.5 text-sm font-semibold text-[#022c56]'
						>
							<i className='fa-solid fa-pen text-xs'></i>
							Edit details
						</button>
						<div className='pt-2'>
							{accountLine}
							{user ? (
								<button type='button' onClick={() => submit(user)} className='qr-btn-primary'>
									Send report
								</button>
							) : (
								<button type='button' onClick={goToLogin} className='qr-btn-primary'>
									Log in to send
								</button>
							)}
						</div>
					</div>
					{viewer}
				</div>
			);
		}

		/* DETAILS — scrolls with the page; Continue sits at the end of the form. */
		const descriptionHint = visibleFields.length
			? 'What is wrong, the exact spot, and since when?'
			: 'Where exactly is it, and since when?';

		return (
			<div className='qr-page'>
				<TopBar title='Report details' onBack={() => setStep('REVIEW')} onRestart={requestStartOver} />
				<div className='w-full max-w-md mx-auto px-[1rem] pt-[1rem] pb-8 flex flex-col gap-[1.25rem]'>
					{photoBlock}
					{errorBanner}

					<div data-invalid={errors.has('authority')}>
						<FieldLabel error={errors.has('authority')}>Send to</FieldLabel>
						{lockedAuthority ? (
							<div className='flex items-center gap-[0.75rem] px-[1rem] py-[0.75rem] rounded-xl bg-blue-50 text-[#022c56]'>
								<i className='fa-solid fa-building-user'></i>
								<span className='min-w-0'>
									<span className='block font-semibold truncate'>{lockedAuthority.name}</span>
									<span className='block text-xs opacity-75 truncate'>{org.fullName}</span>
								</span>
							</div>
						) : org.authorities.length === 0 ? (
							<p className='text-sm text-slate-500 m-0'>No authorities are taking reports here yet.</p>
						) : (
							<AuthorityPicker
								authorities={org.authorities}
								selectedId={authorityId}
								onSelect={(id) => {
									setAuthorityId(id);
									clearError('authority');
								}}
								hasError={errors.has('authority')}
							/>
						)}
					</div>

					<label className='block m-0' data-invalid={errors.has('title')}>
						<FieldLabel error={errors.has('title')}>What is the problem?</FieldLabel>
						<input
							type='text'
							value={title}
							maxLength={TITLE_MAX}
							onChange={(e) => {
								setTitle(e.target.value);
								clearError('title');
							}}
							placeholder='e.g. Washroom not cleaned'
							className={fieldClass(errors.has('title'))}
						/>
					</label>

					<label className='block m-0' data-invalid={errors.has('body')}>
						<FieldLabel error={errors.has('body')}>Tell us more</FieldLabel>
						<textarea
							value={body}
							rows={3}
							maxLength={BODY_MAX}
							onChange={(e) => {
								setBody(e.target.value);
								clearError('body');
							}}
							placeholder={descriptionHint}
							className={`${fieldClass(errors.has('body'))} resize-none`}
						/>
					</label>

					{visibleFields.map((field) => {
						const spec = specForReportField(field);
						const value = fieldValues[field] || '';
						const hasError = errors.has(field);
						const set = (v: string) => {
							setFieldValues((prev) => ({ ...prev, [field]: v }));
							clearError(field);
						};
						return (
							<div key={field} data-invalid={hasError}>
								<FieldLabel error={hasError}>{prettyFieldLabel(field)}</FieldLabel>
								{spec.options ? (
									<OptionChips options={spec.options} value={value} onChange={set} error={hasError} />
								) : (
									<input
										type='text'
										value={value}
										onChange={(e) => set(e.target.value)}
										placeholder={spec.hint}
										className={fieldClass(hasError)}
									/>
								)}
								{spec.helper && <span className='block text-xs text-slate-500 mt-1.5'>{spec.helper}</span>}
							</div>
						);
					})}

					{errors.size > 0 && <p className='text-sm text-red-600 m-0'>Please fill the fields marked in red.</p>}

					<div className='pt-2'>
						{accountLine || (
							<p className='text-xs text-slate-500 text-center mt-0 mb-2'>
								Next: log in or sign up, then check your report and send it.
							</p>
						)}
						<button
							type='button'
							onClick={onContinue}
							disabled={isAuthLoading || org.authorities.length === 0}
							className='qr-btn-primary'
						>
							Continue
						</button>
					</div>
				</div>
				{viewer}
			</div>
		);
	};

	return (
		<>
			{renderStep()}
			{confirmingStartOver && (
				<StartOverSheet
					onCancel={() => setConfirmingStartOver(false)}
					onConfirm={() => {
						setConfirmingStartOver(false);
						startOver();
					}}
				/>
			)}
		</>
	);
}

export default QRReport;
